// =============================================================================
// apps/web - chatbot-page-context.service.ts
// Purpose: Page-level context registry for the chatbot. Pages opt in by
//          calling `registerContext` with module/route/entity info; the
//          chatbot panel reads `currentContext` when building its request
//          so the backend planner + resolvers know what page the user
//          is asking from.
//
//          Opt-in by design — pages that don't register stay context-
//          free and the chatbot still works (just without page-specific
//          grounding).
// =============================================================================

import { Injectable, signal } from '@angular/core';
import type { ChatbotEntityContext } from './chatbot.service';

export type ChatbotContextVariant = 'list' | 'detail' | 'create' | 'edit';

export interface ChatbotPageContext {
  // Stable key used by clearContext on navigate-away. Usually a route token
  // like 'members.list' or 'tasks.detail.<id>'. Lets multiple pages register
  // without stomping on each other.
  key: string;
  module: string;
  route: string;
  variant?: ChatbotContextVariant;
  entityContext?: ChatbotEntityContext | null;
}

@Injectable({ providedIn: 'root' })
export class ChatbotPageContextService {
  /** Most-recently-registered page context. Null when no page is registered. */
  readonly currentContext = signal<ChatbotPageContext | null>(null);

  registerContext(context: ChatbotPageContext): void {
    this.currentContext.set(context);
  }

  /**
   * Clear by key — only the page that owns the current registration can
   * unregister. Prevents a stale page from blowing away a fresher one if
   * lifecycle hooks fire out of order during a fast route change.
   */
  clearContext(key: string): void {
    if (this.currentContext()?.key === key) {
      this.currentContext.set(null);
    }
  }
}
